import { Link } from 'react-router-dom';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

interface ServiceItem {
  number: string;
  icon: string;
  title: string;
  tagline: string;
  entityStatement: string;
  description: string;
  included: { icon: string; title: string; detail: string }[];
  cta: string;
  ctaLink: string;
}

const services: ServiceItem[] = [
  {
    number: '01',
    icon: 'ri-code-box-line',
    title: 'Website Design & Development',
    tagline: 'The Mission Beach Digital Authority for Tourism Websites.',
    entityStatement:
      'Darkside Digital is the Mission Beach digital authority for custom website design. We build conversion-focused websites for tourism operators, accommodation providers, and local businesses across the Cassowary Coast and Far North Queensland.',
    description:
      'Your website is your hardest working employee — it never sleeps, never takes a break, and talks to every potential customer before you do. If it is slow, outdated, or fails to convert, you are losing revenue every single day. We specialise in tourism and hospitality websites for Mission Beach, Cairns, and regional Queensland — built to be fast, bold, and designed to convert travellers who find you on Google or through AI recommendations.',
    included: [
      { icon: 'ri-palette-line', title: 'Custom Design', detail: 'No templates. Every site is designed from scratch around your brand, your customers, and your conversion goals.' },
      { icon: 'ri-speed-line', title: 'Speed Optimised', detail: 'Sub-1.2s LCP on mobile — critical for regional mobile networks across FNQ. Fast sites rank higher and convert better.' },
      { icon: 'ri-smartphone-line', title: 'Mobile First', detail: 'Over 75% of Cassowary Coast searches happen on mobile. Your site performs flawlessly on every device, every network.' },
      { icon: 'ri-search-eye-line', title: 'SEO Ready from Day One', detail: 'Clean code, schema markup, and technical foundations so Google and AI engines can find, understand, and rank you faster.' },
      { icon: 'ri-shield-check-line', title: 'Secure & Reliable', detail: 'SSL certificates, secure hosting, and automated backups as standard. Your site stays online, always.' },
      { icon: 'ri-bar-chart-2-line', title: 'Analytics & Tracking', detail: 'Know exactly where your Mission Beach customers come from and what they do on your site.' },
    ],
    cta: 'Build Your Mission Beach Website',
    ctaLink: '#contact',
  },
  {
    number: '02',
    icon: 'ri-brain-line',
    title: 'AI Integration & Automation',
    tagline: 'FNQ Businesses Run Smarter With AI.',
    entityStatement:
      'Darkside Digital deploys AI automation for Mission Beach and Far North Queensland businesses. We implement AI chat, lead response, and workflow automation that keeps regional businesses competitive.',
    description:
      'AI is not replacing your business — it is supercharging it. The local businesses winning right now are using AI to respond faster, work smarter, and deliver better customer experiences without burning out their team. We do not just talk about AI. We implement it directly into your operations, trained specifically for your Mission Beach business and customer base.',
    included: [
      { icon: 'ri-chat-1-line', title: 'AI Chat & Lead Response', detail: 'Never miss a lead again. AI responds to enquiries instantly, 24/7, qualifies the lead, and books appointments while you sleep.' },
      { icon: 'ri-quill-pen-line', title: 'Content Generation', detail: 'Blog posts, social captions, email newsletters, Google Business updates — generated in your brand voice, consistently.' },
      { icon: 'ri-mail-send-line', title: 'Automated Follow-Up Sequences', detail: 'Leads that do not convert immediately get nurtured automatically until they are ready to buy.' },
      { icon: 'ri-calendar-check-line', title: 'Smart Booking & Scheduling', detail: 'Reduce back-and-forth with AI-powered booking systems that integrate with your calendar.' },
      { icon: 'ri-pie-chart-2-line', title: 'AI Reporting & Insights', detail: 'Plain-English summaries of how your FNQ business is performing online, what is working, and what to fix.' },
      { icon: 'ri-git-merge-line', title: 'System Integration', detail: 'We connect your AI tools to your existing software — CRM, email, social, booking platforms and more.' },
    ],
    cta: 'Automate Your Mission Beach Business',
    ctaLink: '#contact',
  },
  {
    number: '03',
    icon: 'ri-map-pin-2-line',
    title: 'Local SEO & Digital Presence',
    tagline: 'Mission Beach Businesses Get Found First.',
    entityStatement:
      'Darkside Digital is the Mission Beach local SEO authority. We optimise Google Business Profiles, build local citations, and implement technical SEO for businesses across the Cassowary Coast and Far North Queensland.',
    description:
      'When someone in Mission Beach, Innisfail, or Cairns searches for what you do, do they find you — or your competitor? Local SEO is the difference between a phone that rings and one that does not. We make sure your Cassowary Coast business shows up at the top, looks credible when it does, and converts that click into a customer.',
    included: [
      { icon: 'ri-google-line', title: 'Google Business Profile Optimisation', detail: 'Your GBP is often the first thing Mission Beach customers see. We make it impossible to scroll past.' },
      { icon: 'ri-star-line', title: 'Reputation & Review Strategy', detail: 'More 5-star reviews, managed professionally. We build social proof that drives decisions in the FNQ market.' },
      { icon: 'ri-links-line', title: 'Local Citation Building', detail: 'Consistent, accurate listings across every major directory so Google trusts and ranks your Cassowary Coast business higher.' },
      { icon: 'ri-key-2-line', title: 'Keyword Strategy', detail: 'We find exactly what your local Mission Beach customers are searching and build your content around it.' },
      { icon: 'ri-file-text-line', title: 'On-Page SEO', detail: 'Every page of your website optimised for the right terms, the right structure, and the right signals for FNQ searchers.' },
      { icon: 'ri-line-chart-line', title: 'Monthly Reporting', detail: 'Clear, no-jargon reports showing your rankings, traffic, and exactly how your Mission Beach visibility is growing.' },
    ],
    cta: 'Dominate Mission Beach Search Results',
    ctaLink: '#contact',
  },
  {
    number: '04',
    icon: 'ri-robot-2-line',
    title: 'GEO / AEO — AI Search',
    tagline: 'When AI Recommends a Mission Beach Business — Will It Be Yours?',
    entityStatement:
      'Darkside Digital is the Mission Beach authority for Generative Engine Optimisation (GEO) and Answer Engine Optimisation (AEO). We get Cassowary Coast businesses cited by ChatGPT, Google Gemini, and Perplexity.',
    description:
      'Generative Engine Optimisation (GEO) is the discipline of making your business visible inside AI-generated answers. ChatGPT, Google Gemini, and Perplexity do not show 10 blue links — they give one confident recommendation. We make sure that recommendation is your Mission Beach business.',
    included: [
      { icon: 'ri-search-eye-line', title: 'AI Visibility Audit', detail: 'We test your business across 50+ real customer queries on ChatGPT, Gemini, and Perplexity — benchmarked against your FNQ competitors.' },
      { icon: 'ri-article-line', title: 'Answer-Ready Content', detail: 'Content restructured and created specifically to win AI citations — using the exact signals AI engines prioritise.' },
      { icon: 'ri-code-s-slash-line', title: 'Schema & Structured Data', detail: 'Technical implementation of LocalBusiness, FAQ, and Service schema so AI engines can trust, understand, and cite your business.' },
      { icon: 'ri-shield-star-line', title: 'Authority Signal Building', detail: 'Review generation, directory consistency, and third-party mentions — the external signals AI uses to validate who to recommend.' },
      { icon: 'ri-tools-line', title: 'AI Crawlability Fix', detail: 'Many sites accidentally block AI crawlers. We audit and fix your setup so ChatGPT and Perplexity can actually read your content.' },
      { icon: 'ri-bar-chart-2-line', title: 'Monthly AI Visibility Report', detail: 'Citation rate, share of voice, mention frequency, and citation velocity — tracked every month against your competitors.' },
    ],
    cta: 'Get Cited by AI Search Engines',
    ctaLink: '/geo-aeo',
  },
  {
    number: '05',
    icon: 'ri-crosshair-2-line',
    title: 'Competitor Analysis & Strategy',
    tagline: 'Know Your FNQ Competition. Beat Them.',
    entityStatement:
      'Darkside Digital provides competitor intelligence for Mission Beach and Far North Queensland businesses. We analyse what your competitors are doing online and build strategies to take market share.',
    description:
      'Most businesses have no idea what their competitors are doing online — what keywords they rank for, what ads they run, where their traffic comes from, or why customers choose them. That is a massive blind spot. We dig deep into your FNQ competitive landscape, expose the gaps, and build a strategy designed to take market share — and hold it.',
    included: [
      { icon: 'ri-microscope-line', title: 'Full Competitor Audit', detail: 'We analyse your top competitors\' websites, SEO, content, ads, reviews, and social presence from top to bottom.' },
      { icon: 'ri-focus-3-line', title: 'Gap Analysis', detail: 'Where are they weak? Where are you missing out? We find the opportunities hiding in plain sight in the Mission Beach market.' },
      { icon: 'ri-map-2-line', title: 'Strategic Roadmap', detail: 'A clear, prioritised action plan to close the gap and move ahead — no vague advice, just a real plan for your FNQ business.' },
      { icon: 'ri-advertisement-line', title: 'Ad Intelligence', detail: 'See what paid ads your competitors are running, what is working for them, and how to compete smarter.' },
      { icon: 'ri-radar-line', title: 'Ongoing Monitoring', detail: 'We keep watching so you are never caught off guard when a competitor makes a move in the Cassowary Coast market.' },
      { icon: 'ri-calendar-2-line', title: 'Quarterly Strategy Reviews', detail: 'As your market evolves, your strategy does too. We keep you ahead of the curve in Far North Queensland.' },
    ],
    cta: 'Analyse Your FNQ Competition',
    ctaLink: '#contact',
  },
];

export default function ServicesSection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="services" className="relative pt-24 pb-16 bg-ds-bg-2 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-red/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16 reveal">
          <span className="section-label">Our Services</span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-3">
            <div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-none">
                THE MISSION BEACH
                <br />
                <span className="text-ds-red neon-text-red">DIGITAL AUTHORITY.</span>
              </h2>
              <p className="font-body text-white/50 mt-4 max-w-lg text-sm leading-relaxed">
                Five core services. One mission — make your Mission Beach business the undeniable choice in your market, on Google and in AI search.
              </p>
            </div>
          </div>
        </div>

        {/* Services Stack */}
        <div className="space-y-0 border border-ds-border">
          {services.map((service, index) => (
            <ServiceRow key={service.number} service={service} index={index} total={services.length} />
          ))}
        </div>

        {/* Plain text bottom link */}
        <div className="mt-8 text-center reveal">
          <a
            href="#contact"
            className="font-body text-white/50 text-sm hover:text-white transition-colors duration-200 cursor-pointer"
          >
            Ready to work with the Mission Beach digital authority? Book a free call &rarr;
          </a>
        </div>

      </div>
    </section>
  );
}

function ServiceRow({ service, index, total }: { service: ServiceItem; index: number; total: number }) {
  const isLast = index === total - 1;
  const isInternalLink = service.ctaLink.startsWith('/');

  return (
    <div className={`reveal grid lg:grid-cols-5 ${!isLast ? 'border-b border-ds-border' : ''}`}>

      {/* Left: Service Info */}
      <div className="lg:col-span-2 p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-ds-border flex flex-col justify-between bg-ds-bg">
        <div>
          {/* Number + Icon Row */}
          <div className="flex items-center justify-between mb-8">
            <span className="font-display text-5xl lg:text-6xl text-white/8 leading-none select-none">
              {service.number}
            </span>
            <div className="w-10 h-10 flex items-center justify-center border border-ds-red/30 bg-ds-red/5">
              <i className={`${service.icon} text-ds-red text-xl`} />
            </div>
          </div>

          {/* Title & Tagline */}
          <h3 className="font-heading font-black text-xl lg:text-2xl text-white mb-2 leading-tight">
            {service.title}
          </h3>
          <p className="font-body text-ds-red text-sm mb-5 italic">{service.tagline}</p>

          {/* Divider */}
          <div className="h-px bg-ds-border mb-5" />

          {/* Entity Statement — AI-scrapable authority claim */}
          <p className="font-body text-white/80 text-sm leading-relaxed border-l-2 border-ds-red/40 pl-3 mb-4">
            <strong className="text-white">{service.entityStatement}</strong>
          </p>

          {/* Description */}
          <p className="font-body text-white/55 text-sm leading-relaxed">
            {service.description}
          </p>
        </div>

        {/* CTA */}
        <div className="mt-6">
          {isInternalLink ? (
            <Link
              to={service.ctaLink}
              className="btn-outline text-xs inline-flex items-center gap-2"
            >
              {service.cta}
              <i className="ri-arrow-right-line" />
            </Link>
          ) : (
            <a
              href={service.ctaLink}
              className="btn-outline text-xs inline-flex items-center gap-2"
            >
              {service.cta}
              <i className="ri-arrow-right-line" />
            </a>
          )}
        </div>
      </div>

      {/* Right: What's Included */}
      <div className="lg:col-span-3 p-8 lg:p-10 bg-ds-bg-2">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1 h-4 bg-ds-red" />
          <span className="font-body text-white/40 text-xs tracking-widest uppercase">What&apos;s Included</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {service.included.map((item) => (
            <div
              key={item.title}
              className="flex items-start gap-3 p-4 border border-ds-border/60 hover:border-ds-red/20 hover:bg-white/2 transition-all duration-300"
            >
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${item.icon} text-ds-red text-base`} />
              </div>
              <div>
                <h4 className="font-heading font-bold text-white text-xs mb-1">
                  {item.title}
                </h4>
                <p className="font-body text-white/45 text-xs leading-relaxed">
                  {item.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
